/* eslint-disable react/display-name */

import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { clone } from 'ramda';
import { css, StyleSheet } from 'aphrodite/no-important';
import { Waypoint } from 'react-waypoint';
import { BASE_URL, debugLogToConsole, getMonthName, getWeekDay } from '../../utils';
import ChatMessage from './chat-message';
import ChatEditor from './chat-editor';
import { blue, gray } from 'quantux-ui';
import NavbarClone from '../navbar-clone';

const propTypes = {
  chatTitle: PropTypes.string,
  room: PropTypes.object,
  showNewMessgeIcon: PropTypes.bool,
  userName: PropTypes.string,
  onPostMessage: PropTypes.func,
  onUploadFile: PropTypes.func,
  onCloseChat: PropTypes.func
};

const defaultProps = {
  showNewMessgeIcon: false,
  room: {}
};

let scrollToBottom = true;
const MULTIPLIER = 0.0167;
const MULTIPLIER_2 = 0.7;
const batch = Math.ceil(MULTIPLIER * window.innerHeight);
let BATCH_SIZE = batch % 2 === 0 ? batch : batch - 1;
const buffer = Math.ceil(MULTIPLIER_2 * BATCH_SIZE);
let BUFFER_SIZE = buffer % 2 === 0 ? buffer : buffer + 1;
let scrollDirection = 0; // 0 up 1 down
const isIphone = /iPhone|iPod/i.test(navigator.userAgent);

if (isIphone) {
  BATCH_SIZE = 10;
  BUFFER_SIZE = 6;
}
// console.log(`BATCH_SIZE = ${BATCH_SIZE} & BUFFER_SIZE = ${BUFFER_SIZE}`);

const ChatRoom = forwardRef((props, ref) => {
  const [isFileOver, setIsFileOver] = useState(false);
  const [showNewMessageDrawer, toggleNewMessageDrawer] = useState(false);
  const [messages, setMessages] = useState([]);
  const [fromIndex, setFromIndex] = useState(0);
  const [toIndex, setToIndex] = useState(0);
  const [avatars, setAvatars] = useState({});

  const getDmData = () => {
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);
    const formdata = new FormData();
    formdata.append('chatId', props.room._id);
    formdata.append('userName', props.userName);
    formdata.append('skip', 0);
    // formdata.append('limit', props.room.totalMsgs);
    formdata.append('limit', 100);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formdata,
      redirect: 'follow'
    };
    fetch(`${BASE_URL}/chat/getChatRoomMessages`, requestOptions)
      .then(response => response.text())
      .then(async (result) => {
        const res = JSON.parse(result);
        if (res.success) {
          // window.messages = res.messages;
          const usersAvatarsSet = new Set();
          usersAvatarsSet.add(props.room.recipientUserName);
          res.messages.forEach(el => usersAvatarsSet.add(el.senderUserName));
          const avatars = {};
          for (const item of usersAvatarsSet) {
            const url = new URL(`${BASE_URL}/users/getUserAvatarAsBase64`);
            const params = { userName: item };
            url.search = new URLSearchParams(params).toString();
            const res = await fetch(url);
            const json = await res.json();
            if (json.success) {
              avatars[item] = json.avatar;
            } else {
              avatars[item] = '';
            }
          }
          setAvatars(avatars);
          const from = Math.max(0, res.messages.length - BATCH_SIZE);
          const to = res.messages.length - 1;
          console.log(`[ChatRoom.js] from = ${from}, to = ${to}`);
          setMessages(res.messages);
          setFromIndex(from);
          setToIndex(to);
        }
      })
      .catch(err => {
        console.log(err);
      });
  };

  const getGroupChatData = () => {
    const usersAvatarsSet = new Set();
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);
    const formdata = new FormData();
    formdata.append('chatId', props.room._id);
    formdata.append('userName', props.userName);
    formdata.append('userId', props.usr._id);
    // formdata.append('skip', 0);
    // formdata.append('limit', 100);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formdata,
      redirect: 'follow'
    };

    if (props.room && props.room.members && props.room.members.length > 0) {
      for (let i = 0; i < props.room.members.length; i++) {
        const url = new URL(`${BASE_URL}/users/getUserUsername`);
        const params = { userId: props.room.members[i] };
        url.search = new URLSearchParams(params).toString();
        fetch(url)
          .then(response => response.text())
          .then(result => {
            const res = JSON.parse(result);
            if (res.success) {
              usersAvatarsSet.add(res.userName);
            }
          })
          .catch(err => console.log(`Error --- ${err}`));
      }
    }

    fetch(`${BASE_URL}/chat/getGroupChatMessages`, requestOptions)
      .then(response => response.text())
      .then(async (result) => {
        const res = JSON.parse(result);
        if (res.success) {
          // window.messages = res.messages;
          // const usersAvatarsSet = new Set();
          res.messages.forEach(el => usersAvatarsSet.add(el.senderUserName));
          const avatars = {};
          for (const item of usersAvatarsSet) {
            const url = new URL(`${BASE_URL}/users/getUserAvatarAsBase64`);
            const params = { userName: item };
            url.search = new URLSearchParams(params).toString();
            const res = await fetch(url);
            const json = await res.json();
            if (json.success) {
              avatars[item] = json.avatar;
            } else {
              avatars[item] = '';
            }
          }
          setAvatars(avatars);
          const from = Math.max(0, res.messages.length - BATCH_SIZE);
          const to = res.messages.length - 1;
          console.log(`[ChatRoom.js] from = ${from}, to = ${to}`);
          setMessages(res.messages);
          setFromIndex(from);
          setToIndex(to);
        }
      })
      .catch(err => console.log(err));
  };

  useImperativeHandle(ref, () => {
    const handleNewMessage = (msg) => {
      console.log('[Handle new message]');
      console.log(msg);
      const updatedActiveChat = clone(messages);
      updatedActiveChat.push(msg);
      setMessages(updatedActiveChat);
      scrollToBottom = true;
      const from = Math.max(0, updatedActiveChat.length - BATCH_SIZE);
      const to = updatedActiveChat.length - 1;
      console.log(`[ChatRoom.js] (handleOnSendMessage) from = ${from}, to = ${to}`);
      setFromIndex(from);
      setToIndex(to);
    };
    const toggleNewMsgDrawer = () => {
      toggleNewMessageDrawer(true);
    };

    // const scrollBottom = () => {
    //   if (lastItemRef && lastItemRef.current) {
    //     lastItemRef.current.scrollIntoView();
    //   }
    // };
    return { handleNewMessage, toggleNewMsgDrawer };
  });

  useEffect(() => {
    if (props.activeTab === 0) {
      getDmData();
    } else {
      getGroupChatData();
    }
  }, [props.room._id]);

  useEffect(() => {
    if (containerRef && containerRef.current) {
      if (scrollDirection === 0) {
        if (middleItemRef && middleItemRef.current) {
          const val = !isIphone ? middleItemRef.current.offsetTop - 200 : 100;
          console.log(`val = ${val}`);
          containerRef.current.scrollTop = val;
        }
      }
      if (isIphone && scrollDirection !== 0) {
        containerRef.current.scrollTop = 200;
      }
    }
  }, [toIndex]);

  // Refs
  const containerRef = React.createRef();
  const firstItemRef = React.createRef();
  const lastItemRef = React.createRef();
  const middleItemRef = React.createRef();
  window.containerRef = containerRef;
  window.firstItem = firstItemRef;
  window.lastItem = lastItemRef;
  window.middleItem = middleItemRef;

  // Styles
  const styles = StyleSheet.create({
    chatRoomContainer: {
      display: 'flex',
      flexFlow: 'column',
      background: gray[900],
      height: '100%',
      flexGrow: '1',
      // new
      overflowY: 'auto'
      // height: 'calc(100% - 16px)',
      // paddingBottom: '32px',
    },
    chatRoomMessages: {
      overflowY: 'auto',
      padding: '20px 8px 8px',
      height: isIphone ? 'calc(100vh - 160px)' : 'calc(100% - 8px)',
      '-webkit-overflow-scrolling': 'touch'
      // height: 'calc(100vh - 180px)'
      // flexGrow: '1',
      // paddingTop: '0'
      // height: 'calc(100% - 126px)'
      // height: '-webkit-fill-available'
    },
    chatRoomEditor: {
      // position: 'fixed',
      // bottom: '0',
      // left: '0',
      flexShrink: '0'
      // height: '126px'
    },
    floatingBar: {
      padding: '0 4px',
      position: 'fixed',
      width: '100vw',
      bottom: '0px',
      right: '0px',
      height: '40px',
      minHeight: '40px',
      background: gray[800],
      color: 'white',
      alignItems: 'center',
      justifyContent: 'space-between',
      fontSize: '32px',
      zIndex: '1900',
      borderTop: `1px solid ${gray[500]}`
    },
    floatingBar2: {
      padding: '0 4px',
      position: 'fixed',
      width: '100vw',
      top: '0px',
      right: '0px',
      // width: '40px',
      height: '40px',
      minHeight: '40px',
      background: gray[800],
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      borderRadius: '5px',
      fontSize: '32px'
    },
    floatingBarIcon: {
      height: '32px',
      width: '32px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '22px'
    },
    showBtn: {
      background: 'yellow',
      textAlign: 'center',
      height: '40px'
    },
    dateDivider: {
      position: 'relative',
      textAlign: 'center',
      padding: '8px 0',
      top: '13px'
    },
    dateDividerHRuler: {
      height: '1px',
      background: blue[500]
    },
    dateDividerData: {
      position: 'relative',
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '12px',
      border: `1px solid ${blue[500]}`,
      background: gray[900],
      top: '-14px',
      // zIndex: '200',
      fontSize: '12px',
      fontWeight: 'bold',
      color: blue[500]
    }
  });

  const handleOnPostMessage = (msg) => {
    if (props.activeTab === 0) {
      handleOnSendMessage(msg);
    } else {
      handleOnSendGroupChatMessage(msg);
    }
  };

  const handleOnSendMessage = async (msg, isImage = false, isDocument = false, originalFileName) => {
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);
    const formdata = new FormData();
    formdata.append('dmId', props.room._id);
    formdata.append('senderId', props.usr._id);
    formdata.append('senderDisplayName', props.usr.displayName);
    formdata.append('senderUserName', props.usr.userName);
    let tmp = '';
    let tmpId = '';

    if (props.usr.userName === props.room.senderUserName) {
      tmp = props.room.recipientUserName;
      tmpId = props.room.recipient;
    } else {
      tmp = props.room.senderUserName;
      tmpId = props.room.sender;
    }
    formdata.append('recipientUserName', tmp);
    formdata.append('recipientId', tmpId);
    formdata.append('msg', msg);
    formdata.append('isImage', isImage);
    formdata.append('isDocument', isDocument);
    formdata.append('originalFileName', originalFileName);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formdata,
      redirect: 'follow'
    };

    const res = await fetch(`${BASE_URL}/chat/postMessage`, requestOptions).catch(err => console.log(err));
    const data = await res.json();
    if (data.dmId) {
      const updatedActiveChat = clone(messages);
      updatedActiveChat.push(data.msg);
      setMessages(updatedActiveChat);
      scrollToBottom = true;
      const from = Math.max(0, updatedActiveChat.length - BATCH_SIZE);
      const to = updatedActiveChat.length - 1;
      console.log(`[ChatRoom.js] (handleOnSendMessage) from = ${from}, to = ${to}`);
      setFromIndex(from);
      setToIndex(to);
    }
  };

  const handleOnSendGroupChatMessage = async (msg, isImage = false, isDocument = false, originalFileName) => {
    const myHeaders = new Headers();
    const token = localStorage.getItem('token');
    myHeaders.append('Authorization', `Bearer ${token}`);
    const formdata = new FormData();
    formdata.append('chatId', props.room._id);
    formdata.append('senderId', props.usr._id);
    formdata.append('senderDisplayName', props.usr.displayName);
    formdata.append('senderUserName', props.usr.userName);
    formdata.append('msg', msg);
    formdata.append('isImage', isImage);
    formdata.append('isDocument', isDocument);
    formdata.append('originalFileName', originalFileName);

    const requestOptions = {
      method: 'POST',
      headers: myHeaders,
      body: formdata,
      redirect: 'follow'
    };

    const res = await fetch(`${BASE_URL}/chat/postGroupChatMessage`, requestOptions).catch(err => console.log(err));
    const data = await res.json();
    if (data.chatId) {
      const updatedActiveChat = clone(messages);
      updatedActiveChat.push(data.msg);
      setMessages(updatedActiveChat);
      scrollToBottom = true;
      const from = Math.max(0, updatedActiveChat.length - BATCH_SIZE);
      const to = updatedActiveChat.length - 1;
      console.log(`[ChatRoom.js] (handleOnSendMessage) from = ${from}, to = ${to}`);
      setFromIndex(from);
      setToIndex(to);
    }
  };

  const handleFileUpload = (fileUrl, originalFileName) => {
    const isImage = fileUrl.endsWith('.png') || fileUrl.endsWith('.jpg') || fileUrl.endsWith('jpeg');
    if (props.activeTab === 0) {
      handleOnSendMessage(fileUrl, isImage, !isImage, originalFileName);
    } else {
      handleOnSendGroupChatMessage(fileUrl, isImage, !isImage, originalFileName);
    }
  };

  const handleDragEnter = () => { setIsFileOver(true); };

  const handleDragLeave = () => { setIsFileOver(false); };

  const handleDragOver = (e) => {
    e.preventDefault(e);
    setIsFileOver(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsFileOver(false);

    if (e.dataTransfer.items) {
      for (let i = 0; i < e.dataTransfer.items.length; i++) {
        if (e.dataTransfer.items[i].kind === 'file') {
          const img = e.dataTransfer.items[i].getAsFile();
          console.log('img', img);
          console.log('typeof img', typeof img);
          // this.props.onDrop(img);
          const formData = new FormData();
          formData.append('file', img);
          const options = { method: 'POST', body: formData, redirect: 'follow' };
          fetch(`${BASE_URL}/users/uploadImg`, options)
            .then(response => response.text())
            .then(result => {
              const res = JSON.parse(result);
              console.log(res);
              if (res.success) {
                debugLogToConsole(`File uploaded successfully ${res.fileUrl}`);
                handleFileUpload(res.fileUrl, res.originalFileName);
              }
            })
            .catch(error => console.log('error', error));
        }
      }
    }
  };

  const handleOnClickNewMessage = () => {
    toggleNewMessageDrawer(true);
  };

  const loadMoreTopItems = () => {
    if (!scrollToBottom) {
      // const from = Math.max(0, fromIndex - BATCH_SIZE);
      // const from = Math.max(0, fromIndex - BUFFER_SIZE);
      const from = Math.max(0, fromIndex - BUFFER_SIZE / 2);
      let to;
      if (from === 0) {
        // to = BATCH_SIZE - 1;
        to = BUFFER_SIZE - 1;
      } else {
        // to = Math.min(from + BATCH_SIZE - 1, messages.length - 1);
        to = Math.min(from + BUFFER_SIZE - 1, messages.length - 1);
      }
      console.log(`[ChatRoom.js] Top waypoint --> from = ${from}, to = ${to}`);
      scrollDirection = 0;
      setFromIndex(from);
      setToIndex(to);
    }
  };

  const loadMoreBottomItems = (e) => {
    if (!scrollToBottom) {
      // const to = Math.min(toIndex + BUFFER_SIZE, messages.length - 1);
      // const from = Math.max(0, to - BUFFER_SIZE + 1);
      const to = Math.min(toIndex + BUFFER_SIZE / 2, messages.length - 1);
      const from = Math.max(0, to - BUFFER_SIZE + 1);
      console.log(`[ChatRoom.js] *BOTTOM Waypoint* --> from = ${from}, to = ${to}`);
      scrollDirection = 1;
      setFromIndex(from);
      setToIndex(to);
    }
  };

  const renderTopWaypoint = () => {
    if (fromIndex > 0 && messages.length > 0) {
      return (
        <Waypoint onEnter={loadMoreTopItems}>
          <div
            style={{
              color: 'red',
              fontWeight: 'bold',
              background: 'magenta',
              marginBottom: isIphone ? '50px' : '100px'
            }}
          >
            Loading items ...
          </div>
        </Waypoint>
      );
    }
    return null;
  };

  const renderBottomWaypoint = () => {
    if (toIndex < messages.length - 1 && messages.length > 0) {
      return (
        <Waypoint onEnter={loadMoreBottomItems}>
          <div
            style={{
              color: 'red',
              fontWeight: 'bold',
              background: 'magenta',
              marginTop: '100px'
            }}
          >
            Loading items ...
          </div>
        </Waypoint>
      );
    }
    return null;
  };

  const msgs = [];
  if (messages.length > 0) {
    const currentMessages = messages.slice(fromIndex, toIndex + 1);
    let insertDateDivider = true;
    let lastDate = '';
    let msgDate = '';
    currentMessages.forEach((el, idx) => {
      msgDate = new Date(el.dateTime);
      const mDay = getWeekDay(msgDate.getDay());
      const mDate = msgDate.getDate();
      const mMonth = msgDate.getMonth();
      const mYear = msgDate.getFullYear();

      if (idx === 0) {
        lastDate = new Date(el.dateTime);
      }

      if (mDate !== lastDate.getDate() || mMonth !== lastDate.getMonth() || mYear !== lastDate.getFullYear()) {
        insertDateDivider = true;
      }

      if (idx === currentMessages.length - 1) {
        if (insertDateDivider) {
          msgs.push(
            <div className={css(styles.dateDivider)}>
              <div className={css(styles.dateDividerHRuler)}/>
              <div className={css(styles.dateDividerData)}>
                {`${mDay}, ${getMonthName(mMonth)} ${mDate}, ${mYear}`}
              </div>
            </div>
          );
          insertDateDivider = false;
          lastDate = msgDate;
        }
        msgs.push(
          <ChatMessage
            key={el._id}
            msgId={el._id}
            message={el}
            ref={lastItemRef}
            avatarData={avatars[el.senderUserName]}
          />
        );
        // Auto scroll to bottom to make new messages into user view
        if (scrollToBottom) {
          scrollToBottom = false;
          setTimeout(() => {
            if (containerRef && containerRef.current) {
              containerRef.current.scrollTop = containerRef.current.scrollHeight;
            }
            // console.log('Timeout ....');
            // console.log(`lastItem = ${lastItemRef} & lastItem.current = ${lastItemRef.current}`);
            // if (lastItemRef && lastItemRef.current) {
            //   lastItemRef.current.scrollIntoView();
            // }
          }, 2000);
        }
      } else if (idx === 0) {
        if (insertDateDivider) {
          msgs.push(
            <div className={css(styles.dateDivider)}>
              <div className={css(styles.dateDividerHRuler)} />
              <div className={css(styles.dateDividerData)}>
                {`${mDay}, ${getMonthName(mMonth)} ${mDate}, ${mYear}`}
              </div>
            </div>
          );
          insertDateDivider = false;
          lastDate = msgDate;
        }
        msgs.push(
          <ChatMessage
            key={el._id}
            msgId={el._id}
            message={el}
            ref={firstItemRef}
            avatarData={avatars[el.senderUserName]}
          />
        );
      } else {
        if (insertDateDivider) {
          msgs.push(
            <div className={css(styles.dateDivider)}>
              <div className={css(styles.dateDividerHRuler)} />
              <div className={css(styles.dateDividerData)}>
                {`${mDay}, ${getMonthName(mMonth)} ${mDate}, ${mYear}`}
              </div>
            </div>
          );
          insertDateDivider = false;
          lastDate = msgDate;
        }
        if (idx + fromIndex === Math.ceil((fromIndex + toIndex) / 2)) {
          msgs.push(
            <ChatMessage
              key={el._id}
              msgId={el._id}
              message={el}
              ref={middleItemRef}
              avatarData={avatars[el.senderUserName]}
            />
          );
        } else {
          msgs.push(
            <ChatMessage
              key={el._id}
              msgId={el._id}
              message={el}
              avatarData={avatars[el.senderUserName]}
            />
          );
        }
      }
    });
  }

  return (
    <div
      className={`${css(styles.chatRoomContainer)} safari_only`}
      onDragEnter={handleDragEnter}
    >
      {isFileOver
        ? <div
            // onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            style={{
              width: '100%',
              height: '100%',
              position: 'absolute',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'black',
              fontSize: '42px'
            }}
          >
            Drop file to upload
          </div>
        : null
      }

      {/* New message drawer */}
      {showNewMessageDrawer
        ? <ChatEditor
          title={props.chatTitle}
          onSendMessage={handleOnPostMessage}
          onFileUpload={handleFileUpload}
          showNewMessageDrawer={showNewMessageDrawer}
          toggleNewMessageDrawer={toggleNewMessageDrawer}
          />
        : null
      }

      {/* ------------ Chat room messages ------------ */}
      <div
        className={css(styles.chatRoomMessages)}
        ref={containerRef}
      >
        {renderTopWaypoint()}
        {msgs}
        {renderBottomWaypoint()}
      </div>

      {/* Floating navbar */}
      {props.showNewMessgeIcon
        ? <div className={css(styles.floatingBar2)}>
            <NavbarClone />
          </div>
        : null
      }

      {/* Bottom bar */}
      {props.showNewMessgeIcon && isIphone
        ? <div
            className={css(styles.floatingBar)}
            style={{
              display: isIphone ? 'flex' : 'none'
            }}
          >
            <div className='fx fx__v-center'>
              {/* Close chat room icon */}
              <div
                className={`mr-2 ${css(styles.floatingBarIcon)}`}
                onClick={props.onCloseChat}
              >
                <i className='fa fa-chevron-circle-left' />
              </div>
              {props.activeTab === 0
                ? <div
                    className='fx fx__v-center'
                    style={{ height: '40px' }}
                  >
                    <img
                      src={props.avatarUrl}
                      alt="avatar"
                      loading='lazy'
                      style={{
                        width: 'auto',
                        height: '30px',
                        maxHeight: '30px',
                        borderRadius: '5px'
                      }}
                    />
                    <div style={{ fontSize: '1rem', marginLeft: '8px' }}>
                      {props.chatTitle}
                    </div>
                  </div>
                : <div
                    className='fx fx__v-center'
                    style={{ height: '40px' }}
                  >
                    <i
                      className='fa fa-users'
                      style={{ fontSize: '24px' }}
                    />
                    <div style={{ fontSize: '1rem', marginLeft: '8px' }}>
                      {props.chatTitle}
                    </div>
                  </div>
              }
            </div>
            {/* New message icon */}
            <div
              className={css(styles.floatingBarIcon)}
              onClick={handleOnClickNewMessage}
            >
              <i className='fa fa-comment-o' />
            </div>
          </div>
        : null
      }

      {/* New mesage icon desktop */}
      {/* <div
        style={{
          display: isIphone ? 'none' : 'flex',
          position: 'fixed',
          top: '45px',
          right: '45px',
          height: '32px',
          width: '32px',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '22px'
        }}
        onClick={handleOnClickNewMessage}
      >
        <i className='fa fa-comment-o' />
      </div> */}

      {/* Scroll button */}
      {/* {props.showNewMessgeIcon
        ? <div
            style={{
              position: 'fixed',
              bottom: '40px',
              right: '4px',
              padding: '0 4px',
              width: '40px',
              height: '40px',
              color: blue[500],
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '5px',
              fontSize: '32px',
              display: isIphone ? 'flex' : 'none'
            }}
          >
            <i
              onClick={() => {
                containerRef.current.scrollTop = containerRef.current.scrollHeight;
              }}
              className='fa fa-caret-down'
            />
          </div>
        : null
      } */}
    </div>
  );
});

ChatRoom.propTypes = propTypes;
ChatRoom.defaultProps = defaultProps;
export default ChatRoom;
